import React, { useState, useRef, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import uuid from '../../utils/uuidv4'

/**
 * The SiteNavContext holds the open state of the menu
 * and callback functions to control the state.
 * @type {React.Context<{}>}
 */
const SiteNavContext = React.createContext({})

/**
 * Bronson SiteNav component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-site-nav.html
 *
 * @param {SiteNavArea[]} [children] - A collection of {@link SiteNavArea} components.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {string} [closeMenuText] - The label for the close menu toggle.
 * @param {string} [openMenuText] - The label for the open menu toggle.
 * @param {string} [title='Show navigation'] - The title for the menu toggle.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The SiteNav component.
 * @constructor
 */
export function SiteNav({
  children,
  className,
  closeMenuText,
  openMenuText,
  title = 'Show Navigation',
  testId,
  ...otherProps
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [open, setOpen] = useState(null)

  const contextValue = {
    /**
     * The value of open state.
     */
    open,
    /**
     * Callback function to update open state to a new ref object.
     */
    setOpen: (ref) => (ref === open ? setOpen(null) : setOpen(ref)),
    /**
     * Callback function to close the menu.
     */
    closeMenu: () => setMenuOpen(false),
  }

  useEffect(() => {
    const handleEscape = (event) => {
      switch (event.key) {
        case 'Escape':
          setOpen(false)
          break
        default:
      }
    }
    document.addEventListener('keydown', handleEscape, {
      capture: true,
    })

    return () =>
      document.removeEventListener('keydown', handleEscape, {
        capture: true,
      })
  }, [])

  const classNameList = classNames(
    'c-site-nav',
    'js-site-nav',
    {
      'is-active': menuOpen,
    },
    className
  ).trim()

  const panelClassNameList = classNames('c-site-nav__panel', 'js-site-nav__navs', {
    'is-collapsed': !menuOpen,
  }).trim()

  return (
    <div className={classNameList} data-testid={testId} {...otherProps}>
      <button
        className="c-site-nav__toggle js-site-nav__toggle"
        type="button"
        title={title}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-expanded={menuOpen}
      >
        <i className="c-site-nav__toggle-icon" aria-hidden="true" role="img" />
        <span className="c-site-nav__toggle-open-label">{openMenuText || 'Menu'}</span>
        <span className="c-site-nav__toggle-close-label">{closeMenuText || 'Close Menu'}</span>
      </button>
      <div className={panelClassNameList}>
        <nav className="c-site-nav__navs" aria-label="Main Navigation">
          <SiteNavContext.Provider value={contextValue}>{children}</SiteNavContext.Provider>
        </nav>
      </div>
    </div>
  )
}

SiteNav.propTypes = {
  children: PropTypes.node, // Bronson template: 'site-nav'. Use {@link SiteNavArea}.
  className: PropTypes.string, // Additional CSS classes on the component element.
  closeMenuText: PropTypes.string, // Bronson template: 'toggle.label.close'.
  openMenuText: PropTypes.string, // Bronson template: 'toggle.label.open'.
  title: PropTypes.string, // Bronson template: 'toggle.title'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

/**
 * Bronson SiteNavArea component (nested).
 *
 * @param {SiteNavItem[]} [children] - A collection of {@link SiteNavItem} components.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The SiteNavArea component.
 * @constructor
 */
function SiteNavArea({ children, ...otherProps }) {
  return (
    <ul className="c-site-nav__nav" {...otherProps}>
      {children}
    </ul>
  )
}

SiteNavArea.propTypes = {
  children: PropTypes.node, // Bronson template: 'site-nav-item'. Use {@link SiteNavItem}.
}

SiteNavArea.displayName = 'SiteNav.Area'
SiteNav.Area = SiteNavArea

/**
 * Bronson SiteNavItem component (nested).
 *
 * @param {boolean} [active] - Marks the current SiteNavItem link element as active.
 * @param {DropdownItem[]} [children] - A collection of {@link DropdownItem} components.
 * @param {string} [className] - Additional CSS classes on the SiteNavItem link element.
 * @param {string} [dropdownTitle] - Title to display inside the SiteNavItem dropdown element.
 * @param {boolean} [icon] - Flag to display the SiteNavItem icon.
 * @param {string} [label] - The SiteNavItem link label text.
 * @param {boolean} [language] - Flag to activate the language variant.
 * @param {string} [navClassName] - Additional CSS classes on the component element.
 * @param {function} [onClick] - Click handler for the SiteNavItem link element.
 * @param {boolean} [reversed] - Flag to reverse the order.
 * @param {boolean} [topAtBreakpoint] - Flag to show items on top when the {@link SiteNav} is collapsed.
 * @param {string} [testId] - The `[data-testid]` attribute on the link element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The SiteNavItem component.
 * @constructor
 */
function SiteNavItem({
  active,
  children,
  className,
  dropdownTitle,
  icon,
  label,
  language,
  navClassName,
  onClick,
  reversed,
  topAtBreakpoint,
  testId,
  ...otherProps
}) {
  const areaRef = useRef(null)
  const value = useContext(SiteNavContext)

  /**
   * Create a unique ID for dropdown button
   * and dropdown panel.
   */
  const id = uuid()

  /**
   * Handle clicks outside the {@link SiteNavArea}.
   */
  useEffect(() => {
    const outsideClickHandler = (event) => {
      /**
       * Only close the current dropdown when a SiteNavItem is expanded,
       * the event target is not a childElement of the {@link areaRef} and
       * it is not a child of another {@link areaRef} (e.g. multiple navigations)
       * which would also count as an outside click.
       */
      if (
        value?.open &&
        !areaRef?.current?.parentElement?.contains(event?.target) &&
        !event?.target.closest('.c-site-nav__nav')
      ) {
        value.setOpen(null)
      }
    }

    document.addEventListener('mousedown', outsideClickHandler, {
      capture: true,
    })

    /**
     * Cleanup the hook.
     */
    return () =>
      document.removeEventListener('mousedown', outsideClickHandler, {
        capture: true,
      })
  }, [value])

  const navClassNameList = classNames(
    'c-site-nav__item',
    {
      'js-dropdown': children,
      'c-site-nav__item--top-at-breakpoint': topAtBreakpoint,
      'c-site-nav__item--language': language,
    },
    navClassName
  ).trim()

  const classNameList = classNames(
    'c-site-nav__link',
    {
      'js-dropdown__button': children,
      'is-active': active,
      'is-reversed': reversed,
    },
    className
  ).trim()

  /**
   * If there is a {@link DropdownItem},
   * then a `<button>` is rendered, otherwise a `<a>`.
   */
  const TriggerElement = children ? 'button' : 'a'

  function renderIfIcon() {
    if (icon) {
      return <i className="c-site-nav__link-icon" aria-hidden="true" role="img" />
    }
    return null
  }

  function renderIfDropdownTitle() {
    if (dropdownTitle) {
      return <li className="c-site-nav-dropdown__title">{dropdownTitle}</li>
    }
    return null
  }

  function renderIfDropdownItems(open) {
    const dropdownClassNameList = classNames('c-site-nav-dropdown', 'js-dropdown__panel', {
      'is-collapsed': open !== areaRef,
    }).trim()

    if (children) {
      return (
        <nav className={dropdownClassNameList} id={`${id}-dropdown-panel`} aria-labelledby={`${id}-dropdown-button`}>
          <ul className="c-site-nav-dropdown__list">
            {renderIfDropdownTitle()}
            {children}
          </ul>
        </nav>
      )
    }
    return null
  }

  return (
    <li className={navClassNameList} ref={areaRef} {...otherProps}>
      <TriggerElement
        className={classNameList}
        id={children && `${id}-dropdown-button`}
        type={children && 'button'}
        aria-current={active ? 'page' : null}
        aria-controls={children && `${id}-dropdown-panel`}
        aria-expanded={children && value.open === areaRef}
        data-testid={testId}
        tabIndex={children ? null : 0} // Make usable for keyboard-only users when the element is a link.
        role={children ? null : 'link'} // Improving accessibility through semantic mapping when the element is a link.
        /**
         * Map keyboard events to handle the state of the current SiteNavItem.
         */
        onKeyDown={(event) => {
          switch (event.key) {
            case ' ':
            case 'Enter':
              value.setOpen(areaRef)
              break
            case 'Escape':
              value.setOpen(null)
              break
            default:
          }
        }}
        /**
         * Note: `onMouseDown` is applied before `onBlur`, other than `onClick`.
         * If the action is performed in `onClick`, `onBlur` could already be
         * performed, closing another menu. In case of e.g. VW6, closing
         * another menu could move content, so the actual click doesn’t
         * happen anymore, which is an undesired behavior. Test this with
         * VW6 menu and 2 items with subitems, open item 1 and click on
         * item 2 (which will move as item 1 submenu disappears just before).
         */
        onMouseDown={(event) => [
          onClick && [
            /**
             * If there is no {@link DropdownItem},
             * a direct action will be performed.
             */
            !children && [value.setOpen(null), value.closeMenu()],
            /**
             * Perform the click action.
             */
            onClick(event),
          ],
          /**
           * If there is a {@link DropdownItem},
           * then open this item after `onClick`.
           */
          children && [value.setOpen(areaRef)],
        ]}
      >
        <span className="c-site-nav__link-label">{label}</span>
        {renderIfIcon()}
      </TriggerElement>
      {renderIfDropdownItems(value.open)}
    </li>
  )
}

SiteNavItem.propTypes = {
  active: PropTypes.bool, // Bronson template: 'modifier'.
  children: PropTypes.node, // Bronson template: 'dropdown-items'. Use {@link DropdownItem}.
  className: PropTypes.string, // Bronson template: 'modifier'.
  dropdownTitle: PropTypes.string, // Bronson template: 'dropdown-title'.
  icon: PropTypes.bool, // Bronson template: 'has-icon'.
  label: PropTypes.string, // Bronson template: 'label'.
  language: PropTypes.bool, // Bronson template: 'nav-modifier'.
  navClassName: PropTypes.string, // Bronson template: 'nav-modifier'.
  onClick: PropTypes.func, // Added for the `[onClick]` attribute.
  reversed: PropTypes.bool, // Bronson template: 'modifier'.
  topAtBreakpoint: PropTypes.bool, // Bronson template: 'top-at-breakpoint'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

SiteNavItem.displayName = 'SiteNav.Item'
SiteNav.Item = SiteNavItem

/**
 * Bronson DropdownItem component (nested).
 *
 * @param {boolean} [active] - Marks the current item as active.
 * @param {React.ReactNode} [children] - The content to display inside the DropdownItem component.
 * @param {function} [onClick] - The DropdownItem link click handler.
 * @param {string} [testId] - The `[data-testid]` attribute on the link element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The DropdownItem component.
 * @constructor
 */
function DropdownItem({ active, children, onClick, testId, ...otherProps }) {
  const value = useContext(SiteNavContext)

  const classNameList = classNames('c-site-nav-dropdown__link', {
    'is-active': active,
  }).trim()

  return (
    <li className="c-site-nav-dropdown__item" {...otherProps}>
      <a
        className={classNameList}
        aria-current={active ? 'page' : null}
        data-testid={testId}
        tabIndex={0} // Make usable for keyboard-only users.
        role="link" // Improving accessibility through semantic mapping.
        /**
         * Map keyboard events to close the open {@link SiteNavItem}.
         */
        onKeyDown={(event) => {
          switch (event.key) {
            case 'Escape':
              value.setOpen(null)
              break
            default:
          }
        }}
        onClick={(event) =>
          onClick && [
            /**
             * Close the open {@link SiteNavItem} that
             * contains this DropdownItem.
             */
            value.setOpen(null),
            /**
             * Close the whole menu (if the menu is open,
             * e.g. VW6 or on small viewports).
             */
            value.closeMenu(),
            /**
             * Perform the click action.
             */
            onClick(event),
          ]
        }
      >
        {children}
      </a>
    </li>
  )
}

DropdownItem.propTypes = {
  active: PropTypes.bool, // Bronson template: 'is-active'.
  children: PropTypes.node, // Bronson template: 'text'.
  onClick: PropTypes.func, // Added for the `[onClick]` attribute.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

DropdownItem.displayName = 'SiteNavItem.DropdownItem'
SiteNavItem.DropdownItem = DropdownItem
